/**
 * Phaser department lazy lists (homepage).
 *
 * The homepage server-renders FPV product lists only. Lists for 3D Printing
 * and Computers arrive as light placeholders ([data-pdn-lazy], see
 * snippets/phaser-department-lazy.liquid). When one of those departments is
 * shown on <html data-phaser-dept>, this module fetches its sections with the
 * Section Rendering API (up to 5 per request) and swaps the full markup in.
 * Horizon's custom elements upgrade on insertion.
 *
 * Runs on load too, for returning shoppers whose saved department isn't FPV.
 * Hovering or focusing a department button starts the fetch early.
 *
 * Loaded from the placeholder snippet, so it only runs where placeholders
 * exist. Module scripts run once per page however many tags point at them.
 */

const MAX_PER_REQUEST = 5;
const root = document.documentElement;

/** @type {Map<string, Promise<string | null>>} section id -> section HTML */
const cache = new Map();

/** @param {Element} placeholder */
function departmentOf(placeholder) {
  return placeholder.closest('.shopify-section')?.querySelector(':scope > .pdn-only')?.getAttribute('data-pdn-only');
}

/** @param {string} mode */
function placeholdersFor(mode) {
  return [...document.querySelectorAll('[data-pdn-lazy]')].filter(
    (placeholder) => departmentOf(placeholder) === mode,
  );
}

/**
 * Fetch a batch of sections and cache a promise per id.
 * @param {string[]} ids
 */
function fetchBatch(ids) {
  const url = new URL(window.location.pathname, window.location.origin);
  url.searchParams.set('sections', ids.join(','));
  const request = (async () => {
    // One retry after a short pause, so a brief server or network hiccup
    // doesn't leave the shopper with the error line.
    for (let attempt = 0; ; attempt++) {
      try {
        const response = await fetch(url, { headers: { Accept: 'application/json' } });
        if (!response.ok) throw new Error(`Section request failed: ${response.status}`);
        return /** @type {Record<string, string | null>} */ (await response.json());
      } catch (error) {
        if (attempt >= 1) throw error;
        await new Promise((resolve) => setTimeout(resolve, 1500));
      }
    }
  })();

  for (const id of ids) {
    const entry = request.then(
      (sections) => sections[id] ?? null,
      () => null,
    );
    cache.set(id, entry);
    // A failed fetch is not kept, so the next department switch retries it.
    entry.then((html) => {
      if (html === null && cache.get(id) === entry) cache.delete(id);
    });
  }
}

/** @param {string[]} ids */
function request(ids) {
  const missing = ids.filter((id) => !cache.has(id));
  for (let i = 0; i < missing.length; i += MAX_PER_REQUEST) {
    fetchBatch(missing.slice(i, i + MAX_PER_REQUEST));
  }
}

/**
 * Scripts added through a template don't run. Re-create them so any module the
 * section needs is loaded (the browser skips modules it has already run).
 * @param {ParentNode} fragment
 */
function reviveScripts(fragment) {
  for (const old of fragment.querySelectorAll('script')) {
    const script = document.createElement('script');
    for (const { name, value } of old.attributes) script.setAttribute(name, value);
    script.textContent = old.textContent;
    old.replaceWith(script);
  }
}

/**
 * @param {Element} placeholder
 * @param {string | null} html
 */
function swap(placeholder, html) {
  if (!placeholder.isConnected) return;
  const wrapper = placeholder.closest('.shopify-section');
  if (!wrapper) return;

  const template = document.createElement('template');
  if (html) template.innerHTML = html;
  const fresh = template.content.querySelector('.shopify-section');

  // Only swap if the fetch really returned the full list, never another placeholder.
  if (!fresh || fresh.querySelector('[data-pdn-lazy]')) {
    showError(placeholder);
    return;
  }

  // Don't pull the page out from under keyboard users.
  if (wrapper.contains(document.activeElement)) return;

  reviveScripts(fresh);
  wrapper.replaceChildren(...fresh.childNodes);
}

/**
 * Switch a placeholder between loading and failed states.
 * @param {Element} placeholder
 * @param {boolean} failed
 */
function setFailed(placeholder, failed) {
  placeholder.setAttribute('aria-busy', String(!failed));
  placeholder.classList.toggle('pdn-lazy--error', failed);
  placeholder.querySelector('[data-pdn-lazy-status]')?.toggleAttribute('hidden', failed);
  placeholder.querySelector('[data-pdn-lazy-error]')?.toggleAttribute('hidden', !failed);
}

/** @param {Element} placeholder */
function showError(placeholder) {
  setFailed(placeholder, true);
}

/**
 * Load every placeholder for a department.
 * @param {string | null} mode
 * @param {boolean} [render] - false only warms the cache
 */
async function load(mode, render = true) {
  if (!mode) return;
  const placeholders = placeholdersFor(mode);
  if (placeholders.length === 0) return;

  const ids = placeholders.map((placeholder) => placeholder.getAttribute('data-pdn-lazy') || '').filter(Boolean);
  request(ids);
  if (!render) return;

  // A list that failed earlier goes back to loading while it retries.
  for (const placeholder of placeholders) {
    if (placeholder.getAttribute('aria-busy') === 'false') setFailed(placeholder, false);
  }

  await Promise.all(
    placeholders.map(async (placeholder) => {
      const id = placeholder.getAttribute('data-pdn-lazy') || '';
      const html = (await cache.get(id)) ?? null;
      swap(placeholder, html);
    }),
  );
}

function loadActive() {
  load(root.getAttribute('data-phaser-dept'));
}

new MutationObserver(loadActive).observe(root, { attributes: true, attributeFilter: ['data-phaser-dept'] });

// Start fetching when the shopper reaches for a department button.
/** @param {Event} event */
function warm(event) {
  const button = event.target instanceof Element ? event.target.closest('[data-pdn-set]') : null;
  if (button) load(button.getAttribute('data-pdn-set'), false);
}

document.addEventListener('pointerover', warm, { passive: true });
document.addEventListener('focusin', warm);

loadActive();
